import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import moment from 'moment';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

const ROLES = ['super_admin', 'admin', /* 'staff', */ 'trainer'/*, 'student'*/];
const NAVY  = 'var(--tmis-heading)';
const TEAL  = 'var(--tmis-teal-btn)'; // solid fill with white text on top for the active page button
const MUTED = 'var(--tmis-muted)';

export default function UsersPage() {
  const { can, user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [roleFilter, setRoleFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState(null);

  // search / sort / pagination
  const [search, setSearch]       = useState('');
  const [sortCol, setSortCol]     = useState('name');
  const [sortDir, setSortDir]     = useState('asc');
  const [pageSize, setPageSize]   = useState(10);
  const [page, setPage]           = useState(1);
  const PAGE_OPTS = [10, 20, 50, 100, 'ALL'];

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/users', { params: roleFilter ? { role: roleFilter } : {} });
      setUsers(res.data.data || []);
    } catch { toast.error('Failed to load users.'); }
    finally { setLoading(false); }
  }, [roleFilter]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);
  useEffect(() => { setPage(1); }, [search, roleFilter, sortCol, sortDir]);

  // ── filtered + sorted + paginated ──
  const filtered = useMemo(() => {
    if (!search.trim()) return users;
    const q = search.trim().toLowerCase();
    return users.filter(u =>
      u.name.toLowerCase().includes(q) ||
      u.email.toLowerCase().includes(q)
    );
  }, [users, search]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let va = a[sortCol], vb = b[sortCol];
      if (sortCol === 'last_login') {
        va = va ? new Date(va).getTime() : 0;
        vb = vb ? new Date(vb).getTime() : 0;
      } else {
        va = (va ?? '').toString().toLowerCase();
        vb = (vb ?? '').toString().toLowerCase();
      }
      const cmp = va < vb ? -1 : va > vb ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [filtered, sortCol, sortDir]);

  const totalPages = pageSize === 'ALL' ? 1 : Math.max(1, Math.ceil(sorted.length / pageSize));
  const paged = pageSize === 'ALL' ? sorted : sorted.slice((page - 1) * pageSize, page * pageSize);

  function toggleSort(col) {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  }

  function SortTh({ col, label, center }) {
    const active = sortCol === col;
    return (
      <th className={center ? 'text-center' : undefined} onClick={() => toggleSort(col)}
        style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
        {label}
        <i className={`bi ms-1 ${active ? (sortDir === 'asc' ? 'bi-sort-up' : 'bi-sort-down') : 'bi-arrow-down-up'}`}
          style={{ fontSize: '0.7rem', opacity: active ? 1 : 0.6 }} />
      </th>
    );
  }

  async function handleDeactivate(id, name) {
    if (!window.confirm(`Deactivate "${name}"?`)) return;
    try {
      await api.delete(`/api/users/${id}`);
      toast.success('User deactivated.');
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed.'); }
  }

  const roleColor = { super_admin: 'danger', admin: 'primary', staff: 'info', trainer: 'success', student: 'secondary' };

  return (
    <div>
      <div className="page-header d-flex justify-content-between align-items-start flex-wrap gap-3">
        <div>
          <h1>Users</h1>
          <p>Manage system users and roles</p>
        </div>
        {can('users', 'create') && (
          <button className="btn btn-tmis-primary btn-sm" onClick={() => { setEditUser(null); setShowModal(true); }}>
            <i className="bi bi-plus-lg me-1" />Add User
          </button>
        )}
      </div>

      <div className="tmis-card mb-3">
        <div className="d-flex gap-3 flex-wrap align-items-center justify-content-between">
          <div className="filter-tab-group">
            <button className={`filter-tab ${!roleFilter ? 'active' : ''}`} onClick={() => setRoleFilter('')}>All</button>
            {ROLES.filter(r => r !== 'super_admin' || me.role === 'super_admin').map(r => (
              <button key={r} className={`filter-tab ${roleFilter === r ? 'active' : ''}`} onClick={() => setRoleFilter(r)}>
                {r.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
              </button>
            ))}
          </div>
          <div className="input-group" style={{ maxWidth: 280 }}>
            <span className="input-group-text"><i className="bi bi-search" /></span>
            <input
              className="form-control"
              placeholder="Search name or email…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <button className="btn btn-outline-secondary" onClick={() => setSearch('')}>
                <i className="bi bi-x" />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="tmis-card">
        {loading ? (
          <div className="text-center py-4"><div className="spinner-border" style={{ color: '#17a2b8' }} /></div>
        ) : (
          <div className="table-responsive">
            <table className="table tmis-table mb-0">
              <thead>
                <tr>
                  <SortTh col="name" label="Name" />
                  <SortTh col="email" label="Username" />
                  <SortTh col="role" label="Role" center />
                  <SortTh col="department" label="Department" />
                  <SortTh col="last_login" label="Last Login" center />
                  <SortTh col="is_active" label="Status" center />
                  {/* {(can('users', 'update') || can('users', 'delete')) && <th>Actions</th>} */}
                </tr>
              </thead>
              <tbody>
                {paged.map(u => (
                  <tr key={u.id}>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <div className="user-avatar" style={{ width: 30, height: 30, fontSize: '0.75rem' }}>
                          {u.name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase()}
                        </div>
                        <span>{u.name}</span>
                        {u.id === me?.id && <span className="badge bg-light border" style={{ fontSize: '0.65rem', color: '#373839' }}>You</span>}
                      </div>
                    </td>
                    <td>{u.email}</td>
                    <td className="text-center"><span className={`badge bg-${roleColor[u.role] || 'secondary'}`}>{u.role.replace('_', ' ')}</span></td>
                    <td>{u.department || '-'}</td>
                    <td className="text-center" style={{ fontSize: '0.8rem', color: 'var(--tmis-text)' }}>{u.last_login ? moment(u.last_login).format('DD/MM/YY HH:mm') : 'Never'}</td>
                    <td className="text-center">{u.is_active ? <span className="badge bg-success">Active</span> : <span className="badge bg-secondary">Inactive</span>}</td>
                    {/* {(can('users', 'update') || can('users', 'delete')) && (
                      <td>
                        <div className="d-flex gap-1">
                          {can('users', 'update') && <button className="btn btn-sm btn-outline-secondary" onClick={() => { setEditUser(u); setShowModal(true); }}><i className="bi bi-pencil" /></button>}
                          {can('users', 'delete') && u.id !== me?.id && u.is_active && <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeactivate(u.id, u.name)}><i className="bi bi-person-x" /></button>}
                        </div>
                      </td>
                    )} */}
                  </tr>
                ))}
                {!paged.length && (
                  <tr><td colSpan="7" className="text-center py-4 text-muted">
                    {search ? 'No users match your search.' : 'No users found.'}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* ── pagination bar ── */}
        {!loading && sorted.length > 0 && (
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 px-1 pt-3"
            style={{ fontSize: '0.8rem', borderTop: '1px solid var(--tmis-border)', marginTop: 8 }}>
            <div className="d-flex align-items-center gap-2">
              <span style={{ color: MUTED }}>Rows per page:</span>
              {PAGE_OPTS.map(o => (
                <button key={o} onClick={() => setPageSize(o)}
                  style={{
                    padding: '2px 8px', borderRadius: 4, border: '1px solid',
                    borderColor: pageSize === o ? TEAL : 'var(--tmis-border-strong)',
                    background:  pageSize === o ? TEAL : 'var(--tmis-card-bg)',
                    color:       pageSize === o ? 'white' : NAVY,
                    fontWeight:  pageSize === o ? 700 : 400,
                    cursor: 'pointer', fontSize: '0.78rem',
                  }}>
                  {o}
                </button>
              ))}
              <span style={{ color: MUTED, marginLeft: 8 }}>
                {sorted.length} user{sorted.length !== 1 ? 's' : ''}
              </span>
            </div>
            {pageSize !== 'ALL' && totalPages > 1 && (
              <div className="d-flex align-items-center gap-1">
                <button onClick={() => setPage(1)} disabled={page === 1} className="btn btn-sm btn-outline-secondary" style={{ padding: '2px 7px' }}><i className="bi bi-chevron-double-left" /></button>
                <button onClick={() => setPage(p => p - 1)} disabled={page === 1} className="btn btn-sm btn-outline-secondary" style={{ padding: '2px 7px' }}><i className="bi bi-chevron-left" /></button>
                <span style={{ color: MUTED, minWidth: 80, textAlign: 'center' }}>Page {page} of {totalPages}</span>
                <button onClick={() => setPage(p => p + 1)} disabled={page === totalPages} className="btn btn-sm btn-outline-secondary" style={{ padding: '2px 7px' }}><i className="bi bi-chevron-right" /></button>
                <button onClick={() => setPage(totalPages)} disabled={page === totalPages} className="btn btn-sm btn-outline-secondary" style={{ padding: '2px 7px' }}><i className="bi bi-chevron-double-right" /></button>
              </div>
            )}
          </div>
        )}
      </div>

      {showModal && <UserModal user={editUser} onClose={() => setShowModal(false)} onSaved={() => { setShowModal(false); fetchUsers(); }} />}
    </div>
  );
}

function UserModal({ user, onClose, onSaved }) {
  const isEdit = !!user;
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'staff', phone: '', department: '', is_active: 1 });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isEdit) setForm({ name: user.name || '', email: user.email || '', password: '', role: user.role || 'staff', phone: user.phone || '', department: user.department || '', is_active: user.is_active ?? 1 });
  }, [isEdit, user]);

  function set(k, v) { setForm(p => ({ ...p, [k]: v })); setError(''); }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name || !form.email || !form.role) { setError('Name, email, and role are required.'); return; }
    if (!isEdit && !form.password) { setError('Password is required for new users.'); return; }
    setSaving(true);
    try {
      if (isEdit) { await api.put(`/api/users/${user.id}`, form); toast.success('User updated.'); }
      else { await api.post('/api/users', form); toast.success('User created.'); }
      onSaved();
    } catch (err) { setError(err.response?.data?.message || 'Failed.'); }
    finally { setSaving(false); }
  }

  return (
    <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{isEdit ? 'Edit User' : 'Add User'}</h5>
            <button className="btn-close btn-close-white" onClick={onClose} />
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {error && <div className="alert alert-danger inline-error mb-3">{error}</div>}
              <div className="row g-3">
                <div className="col-12"><label className="form-label">Full Name <span className="text-danger">*</span></label><input className="form-control" value={form.name} onChange={e => set('name', e.target.value)} required /></div>
                <div className="col-12"><label className="form-label">Email <span className="text-danger">*</span></label><input type="email" className="form-control" value={form.email} onChange={e => set('email', e.target.value)} required /></div>
                <div className="col-md-6"><label className="form-label">Password {!isEdit && <span className="text-danger">*</span>}</label><input type="password" className="form-control" value={form.password} onChange={e => set('password', e.target.value)} placeholder={isEdit ? 'Leave blank to keep' : ''} /></div>
                <div className="col-md-6"><label className="form-label">Role <span className="text-danger">*</span></label>
                  <select className="form-select" value={form.role} onChange={e => set('role', e.target.value)}>
                    {ROLES.map(r => <option key={r} value={r}>{r.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>)}
                  </select>
                </div>
                <div className="col-md-6"><label className="form-label">Phone</label><input className="form-control" value={form.phone} onChange={e => set('phone', e.target.value)} /></div>
                <div className="col-md-6"><label className="form-label">Department</label><input className="form-control" value={form.department} onChange={e => set('department', e.target.value)} /></div>
                {isEdit && <div className="col-12">
                  <div className="form-check form-switch">
                    <input className="form-check-input" type="checkbox" id="u_active" checked={!!form.is_active} onChange={e => set('is_active', e.target.checked ? 1 : 0)} />
                    <label className="form-check-label" htmlFor="u_active">Active</label>
                  </div>
                </div>}
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-tmis-primary" disabled={saving}>
                {saving && <span className="spinner-border spinner-border-sm me-1" />}
                {isEdit ? 'Save Changes' : 'Create User'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}